import type { ApiLocation, ApiUnit, Category, CategoryStyle } from "./model";

/**
 * Kategori + kat filtresinin ORTAK durumu. İki filtre tek state paylaşır;
 * eşleştirme LOKASYON değil ÜNİTE düzeyinde yapılır — bir lokasyon, ancak
 * **tek bir ünitesi** hem seçili kategoriyi hem seçili katı sağlıyorsa eşleşir.
 */
export interface FilterState {
  cats: ReadonlySet<Category>;
  /** null = "All" (her kat) */
  level: string | null;
}

/** Filtreyi birlikte (AND) sağlayan üniteler. */
export function matchingUnits(loc: ApiLocation, state: FilterState): ApiUnit[] {
  return loc.units.filter(
    (u) => state.cats.has(u.category) && (state.level === null || u.floorLevel === state.level),
  );
}

/**
 * Temsilci seçiminin TEK ölçütü: önce en alt kat, eşitlikte kategori adı.
 *
 * Parsel rengi (`representativeUnit`) ile görünen rozet (`selectBadge`) bunu
 * PAYLAŞMAK zorunda. Aksi halde aynı katta iki kategori olan yerlerde (Terminal:
 * Level 0'da iki dükkân + bir ofis) parsel dükkân rengine boyanıp üstünde ofis
 * rozeti taşıyordu; gövdeye ve rozete tıklamak farklı kiracı açıyordu.
 */
function rank(
  aOrder: number, aCount: number, aCat: Category,
  bOrder: number, bCount: number, bCat: Category,
): number {
  // 1) en alt kat  2) o katın BASKIN kullanımı (en çok kiracısı olan kategori)
  // 3) son çare: kategori adı — hep aynı sonucu vermek için.
  return aOrder - bOrder || bCount - aCount || aCat.localeCompare(bCat);
}

/** Lokasyon içinde (kat + kategori) başına kiracı sayısı. */
function unitCounts(loc: ApiLocation): Map<string, number> {
  const m = new Map<string, number>();
  for (const u of loc.units) {
    const k = `${u.floorLevel}\u0000${u.category}`;
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

/**
 * Filtre altında lokasyonu temsil eden ünite: EŞLEŞENLER arasında `rank`'e göre
 * ilk gelen. Binanın giriş katı değil — "Offices" seçiliyken üst kattaki ofis
 * temsilci olur, böylece renk filtrelenen şeyi gösterir.
 */
export function representativeUnit(loc: ApiLocation, state: FilterState): ApiUnit | undefined {
  const matches = matchingUnits(loc, state);
  if (!matches.length) return undefined;
  const counts = unitCounts(loc);
  const n = (u: ApiUnit) => counts.get(`${u.floorLevel}\u0000${u.category}`) ?? 1;
  return matches.reduce((best, u) =>
    rank(u.floorOrder, n(u), u.category, best.floorOrder, n(best), best.category) < 0 ? u : best,
  );
}

/**
 * Belirli bir ünitenin rengi: colorMode custom ise lokasyonun özel rengi,
 * değilse ünitenin kategori rengi. Parsel dolgusu ve rozet rengi BUNDAN türer
 * ki ikisi asla ayrışmasın.
 */
export function fillForUnit(
  loc: ApiLocation,
  unit: ApiUnit,
  stylesByCategory: Map<Category, CategoryStyle>,
  fallback = "#e5e7eb",
): string {
  if (loc.colorMode === "custom" && loc.customFill) return loc.customFill;
  return stylesByCategory.get(unit.category)?.fill ?? fallback;
}

/**
 * Parselin filtre altındaki dolgu rengi; eşleşme yoksa `null`
 * (çağıran taraf parseli soluklaştırır).
 */
export function filteredFill(
  loc: ApiLocation,
  state: FilterState,
  stylesByCategory: Map<Category, CategoryStyle>,
  fallback = "#e5e7eb",
): string | null {
  const rep = representativeUnit(loc, state);
  if (!rep) return null;
  return fillForUnit(loc, rep, stylesByCategory, fallback);
}

/**
 * `floorOrder` alanı veride İKİ FARKLI konvansiyonla kullanılıyor:
 *  - kat kodu gibi: Level 0=0, Mezzanine=50, Level 1=100, Level 2=200 …
 *  - basit sıra numarası gibi: 0, 1, 2 (ör. tek bir binanın kendi içinde)
 * İkisi de kendi içinde tutarlı; hangisinin "doğru" olduğuna dair bir kural yok.
 *
 * Ama alan LOKASYONLAR ARASI ortak sıralama anahtarı olarak kullanılınca bu
 * karışım güvenilmez oluyor: bir binanın "Level 1"i 1, ötekinin 100 olabiliyor.
 * Aynı şekilde bir binanın tüm üniteleri aynı sayıya sahipse (hepsi 100) o bina
 * içinde hangi katın altta olduğu sayıdan okunamıyor.
 *
 * Bu yüzden sıralama SAYIYA değil KAT ADINA dayandırılır: her kat adına, o adın
 * veride en çok aldığı değer kanonik sıra olarak atanır (eşitlikte en küçüğü).
 * Kaynak yine verinin kendisi — kodda gömülü bir kat tablosu yok. Veri
 * DEĞİŞTİRİLMEZ; yalnızca okunurken tek konvansiyona indirgenir. Bir lokasyonun
 * kendi içindeki göreli sıra korunur (kararlı sıralama).
 */
export function normalizeFloorOrders<T extends { locations: ApiLocation[] }>(data: T): T {
  const seen = new Map<string, Map<number, number>>(); // kat → (order → kaç kez)
  for (const loc of data.locations) {
    for (const u of loc.units) {
      const counts = seen.get(u.floorLevel) ?? new Map<number, number>();
      counts.set(u.floorOrder, (counts.get(u.floorOrder) ?? 0) + 1);
      seen.set(u.floorLevel, counts);
    }
  }
  const canonical = new Map<string, number>();
  for (const [level, counts] of seen) {
    let bestOrder = Infinity, bestCount = -1;
    for (const [order, n] of counts) {
      if (n > bestCount || (n === bestCount && order < bestOrder)) {
        bestOrder = order;
        bestCount = n;
      }
    }
    canonical.set(level, bestOrder);
  }
  return {
    ...data,
    locations: data.locations.map((loc) => ({
      ...loc,
      units: loc.units.map((u) => ({ ...u, floorOrder: canonical.get(u.floorLevel) ?? u.floorOrder })),
    })),
  };
}

/**
 * Haritadaki distinct katlar, `floorOrder`'a göre sıralı. Aynı kat adı farklı
 * order'larla kayıtlıysa EN KÜÇÜĞÜ esas alınır → sıra deterministik kalır.
 */
export function levelOptions(locations: readonly ApiLocation[]): string[] {
  const orderByLevel = new Map<string, number>();
  for (const loc of locations) {
    for (const u of loc.units) {
      const cur = orderByLevel.get(u.floorLevel);
      if (cur === undefined || u.floorOrder < cur) orderByLevel.set(u.floorLevel, u.floorOrder);
    }
  }
  return [...orderByLevel.entries()]
    .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
    .map(([level]) => level);
}

export interface BadgeInfo {
  level: string;
  order: number;
  /** Bu kat+kategoride kaç kiracı var — katın baskın kullanımını belirler. */
  count: number;
  category: Category;
  name: string;
  logo: string | null;
}

/**
 * Lokasyonun rozetleri: kat BAŞINA DEĞİL, **kat + kategori** başına bir rozet.
 * (Terminal ve Forni 2F'de aynı katta hem ofis hem dükkân var; kat bazlı
 * gruplama bunlardan birini görünmez kılıyor ve rengi rastgeleleştiriyordu.)
 * `floorOrder`, sonra kategori adına göre sıralı → deterministik.
 */
export function badgeInfos(loc: ApiLocation): BadgeInfo[] {
  const m = new Map<string, BadgeInfo>();
  for (const u of loc.units) {
    const key = `${u.floorLevel} ${u.category}`;
    const cur = m.get(key);
    if (!cur) {
      m.set(key, {
        level: u.floorLevel,
        order: u.floorOrder,
        count: 1,
        category: u.category,
        name: u.tenantName ?? loc.name,
        logo: u.logo,
      });
    } else {
      cur.count += 1;
      if (u.tenantName && cur.name === loc.name) cur.name = u.tenantName; // isimli kiracıyı tercih et
      if (!cur.logo && u.logo) cur.logo = u.logo;                          // logosu olan kiracıyı tercih et
    }
  }
  return [...m.values()].sort((a, b) => rank(a.order, a.count, a.category, b.order, b.count, b.category));
}

/**
 * Bir rozet "slot"unda filtre altında gösterilecek TEK rozet: filtreyi sağlayan
 * rozetler arasındaki en düşük `floorOrder`. Hiçbiri sağlamıyorsa rozet yok.
 */
export function selectBadge(badges: readonly BadgeInfo[], state: FilterState): BadgeInfo | undefined {
  const matches = badges.filter(
    (b) => state.cats.has(b.category) && (state.level === null || b.level === state.level),
  );
  if (!matches.length) return undefined;
  return matches.reduce((best, b) =>
    rank(b.order, b.count, b.category, best.order, best.count, best.category) < 0 ? b : best,
  );
}

/**
 * Sidebar açılırken seçilecek ünitenin indeksi. Kat VE kategori birlikte
 * aranır; bulunamazsa yalnız kat, o da yoksa ilk ünite.
 *
 * Kategori şart: aynı katta birden fazla kiracı olabiliyor (Terminal'de Level 0'da
 * iki dükkân + bir ofis). Yalnız kata bakmak ofis rozetine tıklandığında ilk
 * dükkânı açıyordu.
 */
export function pickUnitIndex(
  units: readonly ApiUnit[],
  level: string | undefined,
  category: Category | undefined,
): number {
  if (level && category) {
    const both = units.findIndex((u) => u.floorLevel === level && u.category === category);
    if (both >= 0) return both;
  }
  if (category && !level) {
    const byCat = units.findIndex((u) => u.category === category);
    if (byCat >= 0) return byCat;
  }
  if (level) {
    const byLevel = units.findIndex((u) => u.floorLevel === level);
    if (byLevel >= 0) return byLevel;
  }
  return 0;
}

/** Kategori başına ünite sayısı — seçili kat varsa yalnızca o kattakiler. */
export function categoryCounts(
  locations: readonly ApiLocation[],
  level: string | null,
): Map<Category, number> {
  const counts = new Map<Category, number>();
  for (const loc of locations) {
    for (const u of loc.units) {
      if (level !== null && u.floorLevel !== level) continue;
      counts.set(u.category, (counts.get(u.category) ?? 0) + 1);
    }
  }
  return counts;
}
